import * as React from "react";
import { Theme, useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import withStyles from "@mui/styles/withStyles";
import {
  Typography,
  Paper,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  TableSortLabel,
  Tooltip,
  Checkbox,
  TableContainer,
} from "@mui/material";
import { KeyValue, SortProperties, TableHeader } from "../../interfaces";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export interface Props {
  tableBody: React.ReactElement;
  tableHeaders: TableHeader[][];
  sortProperties?: SortProperties;
  headerCheckbox?: KeyValue;
  useStickyHeader?: boolean;
}

const LightTooltip = withStyles((theme: Theme) => ({
  arrow: {
    color: theme.palette.common.white,
  },
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    boxShadow: theme.shadows[1],
    border: "1px solid",
    borderColor: theme.palette.primary.dark,
    padding: 10,
    fontSize: 14,
    textAlign: "center",
  },
}))(Tooltip);

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: "100%",
    marginBottom: "1rem",
  },
  tableWrapper: {
    overflow: "auto",
  },
  toolTipText: {
    fontWeight: 700,
    cursor: "pointer",
  },
  subTitle: {
    fontWeight: 700,
    lineHeight: "1rem",
    cursor: "pointer",
    color: "#FFFFFF",
  },
  CheckboxHeader: {
    "&.MuiTableCell-head": {
      textAlign: "left",
    },
    "& svg": {
      fill: "white",
    },
  },
  multiHeaderTable: {
    "& .Mui-active": {
      color: theme.palette.common.white,
    },
    "& thead": {
      "& .MuiTableCell-head": {
        lineHeight: "1.25rem",
      },
      "& th, td": {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.primary.main,
        textAlign: "center",
        fontWeight: "bold",
        border: "none",
        maxHeight: 10,
      },
      "& tr": {
        "&:nth-child(odd)": {
          "& th": {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
          },
        },
      },
      "& .MuiTableSortLabel-icon": {
        opacity: 1,
        transition: "none",
      },
      "& .MuiTableSortLabel-root.MuiTableSortLabel-active": {
        color: theme.palette.common.white,
      },
      "& .MuiTableSortLabel-root:hover, .MuiTableSortLabel-root:focus": {
        color: theme.palette.common.white,
      },
      "& .fa-sort, .fa-sort-down, .fa-sort-up": {
        margin: "0 10px 0 0",
      },
    },
    '& #multi-header-table-body > tr[id*="header-row"]': {
      "backgroundColor": "#f1eee6",
      "& td": {
        color: "#70633e",
      },
    },
    "& tbody": {
      '& tr[id*="marketSelectionRow"]': {
        "&:nth-child(odd)": {
          backgroundColor: "#ecffff",
        },
      },
      "& tr": {
        height: 34,
      },
      '& tr[id*="-empty-row"]': {
        height: 24,
      },
      "& td": {
        border: "none",
        color: theme.palette.primary.main,
        textAlign: "center",
      },
    },
  },
}));

const HeaderToolTip = (header: TableHeader, classes: KeyValue) => {
  const headerTitle = header.subTitle ? (
    <div>
      <Typography className={classes.subTitle}>{header.title}</Typography>
      <Typography className={classes.subTitle}>{header.subTitle}</Typography>
    </div>
  ) : (
    header.title
  );
  return header.toolTip ? (
    <LightTooltip arrow placement="top" title={header.toolTip}>
      <Typography className={classes.toolTipText}>{headerTitle}</Typography>
    </LightTooltip>
  ) : (
    headerTitle
  );
};

const HeaderCell = (
  header: TableHeader,
  index: number,
  classes: KeyValue,
  sortProperties?: SortProperties,
  headerCheckbox?: KeyValue,
) => {
  const noCheckbox = header.title !== "" && typeof header.title === "string";
  const id =
    header.title !== " " && typeof header.title === "string"
      ? `${header.title
          .replace(/[^\w\s]/gi, "-")
          .replace(/ /, "-")
          .toLowerCase()}-header-${index}`
      : `header-cell-${index}`;
  const sortDirection =
    sortProperties && sortProperties.sortDirection !== ""
      ? (sortProperties.sortDirection.toLowerCase() as "asc" | "desc")
      : false;
  const emptyHeaderProps =
    header.title !== " " || "" ? {} : { "aria-label": "empty" };
  const tableSortIcon: React.ComponentType<{ className: string }> = () => {
    return sortProperties && header.apiField === sortProperties.sortField ? (
      sortDirection === "asc" ? (
        <FontAwesomeIcon icon="sort-up" />
      ) : (
        <FontAwesomeIcon icon="sort-down" />
      )
    ) : (
      <FontAwesomeIcon icon="sort" />
    );
  };

  return (
    <TableCell
      align={"right"}
      style={header.styles}
      className={
        index === 0 && headerCheckbox ? classes.CheckboxHeader : undefined
      }
      colSpan={header.colSpan}
      {...emptyHeaderProps}
      component="th"
      id={id}
      key={id}
      sortDirection={sortDirection}
    >
      <>
        {index === 0 && headerCheckbox && noCheckbox && (
          <Checkbox
            color="secondary"
            checked={headerCheckbox.checked}
            onChange={() => {
              headerCheckbox.checked
                ? headerCheckbox.checkboxClick(false)
                : headerCheckbox.checkboxClick(true);
            }}
            inputProps={{
              "aria-label": "select all",
            }}
          />
        )}
        {header.apiField && sortProperties ? (
          <TableSortLabel
            id={`sort-handler-${index}`}
            style={
              header.styles && header.styles.width === "full-width"
                ? { whiteSpace: "nowrap" }
                : {}
            }
            IconComponent={tableSortIcon}
            active={header.apiField === sortProperties.sortField}
            direction={sortDirection ? sortDirection : "asc"}
            onClick={() => {
              if (sortProperties) {
                sortProperties.sortHandler(header);
              }
            }}
          >
            {HeaderToolTip(header, classes)}
          </TableSortLabel>
        ) : (
          HeaderToolTip(header, classes)
        )}
      </>
    </TableCell>
  );
};

function SummaryTable(props: Props) {
  const classes = useStyles(useTheme);
  return props.useStickyHeader ? (
    <TableContainer
      component={Paper}
      sx={{ maxHeight: 750, maxWidth: "100%", zIndex: 3 }}
    >
      <Table className={classes.multiHeaderTable} stickyHeader>
        <TableHead key="multi-header-table-head" id={"multi-header-table-head"}>
          {props.tableHeaders.map(
            (headers: TableHeader[], rowIndex: number) => (
              <TableRow
                key={`header-${rowIndex}`}
                sx={{
                  position: "sticky",
                  top: rowIndex * 37,
                  backgroundColor: "#fff",
                }}
              >
                {headers.map((header: TableHeader, colIndex: number) =>
                  HeaderCell(
                    header,
                    colIndex,
                    classes,
                    props.sortProperties,
                    props.headerCheckbox,
                  ),
                )}
              </TableRow>
            ),
          )}
        </TableHead>
        <TableBody id="multi-header-table-body">{props.tableBody}</TableBody>
      </Table>
    </TableContainer>
  ) : (
    <Paper className={classes.root} elevation={0}>
      <div className={classes.tableWrapper} id="tableWrapper">
        <Table className={classes.multiHeaderTable} id={"multi-header-table"}>
          <TableHead
            key="multi-header-table-head"
            id={"multi-header-table-head"}
          >
            {props.tableHeaders.map((headers: TableHeader[], index: number) => {
              return (
                <TableRow key={`header-${index}`}>
                  {headers.map((header: TableHeader, index: number) => {
                    return HeaderCell(
                      header,
                      index,
                      classes,
                      props.sortProperties,
                      props.headerCheckbox,
                    );
                  })}
                </TableRow>
              );
            })}
          </TableHead>
          <TableBody id="multi-header-table-body">{props.tableBody}</TableBody>
        </Table>
      </div>
    </Paper>
  );
}

class MultiHeaderTable extends React.Component<Props> {
  render() {
    const tableHeaders = this.props.tableHeaders;
    const multiHeaderTableData = this.props.tableBody;
    if (!tableHeaders || tableHeaders.length === 0 || !multiHeaderTableData) {
      return <div>Loading...</div>;
    } else {
      return (
        <SummaryTable
          tableHeaders={tableHeaders}
          tableBody={multiHeaderTableData}
          sortProperties={this.props.sortProperties}
          headerCheckbox={this.props.headerCheckbox}
          useStickyHeader={this.props.useStickyHeader}
        />
      );
    }
  }
}

export default MultiHeaderTable;
